import type { ConversationStep } from '../types';
import type { FormValues } from '../../promotions/types';
import {
  sellerOptions,
  cnpjOptions,
  eanOptions,
  skuOptions,
  brandOptions,
  categoryOptions,
  subcategoryOptions,
  departmentOptions,
  industryOptions,
  familyOptions,
} from '../../promotions/data/mockOptions';


const promoNameStep: ConversationStep = {
  fieldKey: 'nomePromo',
  question: 'Como vamos chamar essa promoção **internamente**? (nome de controle)',
};

const displayNameStep: ConversationStep = {
  fieldKey: 'nomeExibicao',
  question: 'Qual o nome que será **exibido para os clientes** no site e app?',
};

const descriptionStep: ConversationStep = {
  fieldKey: 'descricao',
  question: 'Descreva a promoção em até **250 caracteres** (benefício, regras e duração):',
  multiline: true,
};

const internalDescStep: ConversationStep = {
  fieldKey: 'descricaoInterna',
  question: 'Há alguma **descrição interna** de campanha (controle interno, não aparece no site)? Se não, pode pular digitando **-**.',
};

const industryStep: ConversationStep = {
  fieldKey: 'industria',
  question: 'De qual **indústria** é essa promoção?',
  options: industryOptions,
  multiple: true,
};

const multiSponsorStep: ConversationStep = {
  fieldKey: 'multiPatrocinador',
  question: 'Essa promoção tem **múltiplos patrocinadores**?',
  quickReplies: [
    { label: 'Não', value: '0' },
    { label: 'Sim', value: '1' },
  ],
};

const sponsorStep: ConversationStep = {
  fieldKey: 'patrocinadores',
  question: 'Quem são os **patrocinadores** e qual a contribuição de cada um? (ex: Unilever 100%)',
};

const cumulativeStep: ConversationStep = {
  fieldKey: 'cumulativa',
  question: 'Essa promoção é **cumulativa** com outras promoções?',
  quickReplies: [
    { label: 'Sim', value: '1' },
    { label: 'Não', value: '2' },
  ],
};

const startDateStep: ConversationStep = {
  fieldKey: 'inicioVigencia',
  question: 'Qual a **data e hora de início** da promoção?',
  isDateTime: true,
};

const endDateStep: ConversationStep = {
  fieldKey: 'fimVigencia',
  question: 'Qual a **data e hora de término** da promoção?',
  isDateTime: true,
};

const budgetLinkedStep: ConversationStep = {
  fieldKey: 'budgetAtrelado',
  question: 'Existe um **budget (orçamento)** atrelado a essa promoção?',
  quickReplies: [
    { label: 'Sim', value: '1' },
    { label: 'Não', value: '2' },
  ],
};

const budgetValueStep: ConversationStep = {
  fieldKey: 'budgetValor',
  question: 'Qual o valor do **budget em R$**? (apenas o número, ex: 100000)',
  skipIf: (v) => v['budgetAtrelado'] !== '1',
};

const sellersStep: ConversationStep = {
  fieldKey: 'sellersIncluidos',
  question: 'Quais **sellers** estão incluídos? Selecione da lista ou escreva **All** para todos.',
  options: sellerOptions,
  multiple: true,
};

const sellersExceptionStep: ConversationStep = {
  fieldKey: 'sellersExcecao',
  question: 'Algum seller deve ser **excluído**? Selecione ou responda **-** se não houver.',
  options: sellerOptions,
  multiple: true,
};

const cnpjsStep: ConversationStep = {
  fieldKey: 'cnpjsIncluidos',
  question: 'Há CNPJs específicos de **varejistas incluídos**? Selecione ou responda **-** se não houver restrição.',
  options: cnpjOptions,
  multiple: true,
};

const channelsStep: ConversationStep = {
  fieldKey: 'canais',
  question: 'Em quais **canais** a promoção será veiculada?',
  quickReplies: [
    { label: 'Site (1)', value: '1' },
    { label: 'App (2)', value: '2' },
    { label: 'WhatsApp (3)', value: '3' },
    { label: 'Site + App', value: '1,2' },
    { label: 'Todos', value: '1,2,3' },
  ],
};

const orderTypeStep: ConversationStep = {
  fieldKey: 'tipoPedido',
  question: 'Qual o **tipo de pedido** que se aplica?',
  quickReplies: [
    { label: 'Autônomo', value: '1' },
    { label: 'Venda assistida', value: '2' },
    { label: 'Ambos', value: '1,2' },
  ],
};

const discountCriteriaStep: ConversationStep = {
  fieldKey: 'criterioDesconto',
  question: 'O gatilho do desconto é por **quantidade** ou por **valor de compra**?',
  quickReplies: [
    { label: 'Quantidade mín/máx', value: '1' },
    { label: 'Valor mín/máx (R$)', value: '2' },
  ],
};

const minQtyStep: ConversationStep = {
  fieldKey: 'qtdMinima',
  question: 'Qual a **quantidade mínima** de itens para ativar o desconto?',
  skipIf: (v) => v['criterioDesconto'] !== '1',
};

const maxQtyStep: ConversationStep = {
  fieldKey: 'qtdMaxima',
  question: 'Qual a **quantidade máxima** de itens? (deixe em branco se ilimitado)',
  skipIf: (v) => v['criterioDesconto'] !== '1',
};

const minValueStep: ConversationStep = {
  fieldKey: 'valorMinimo',
  question: 'Qual o **valor mínimo em R$** para ativar o desconto?',
  skipIf: (v) => v['criterioDesconto'] !== '2',
};

const maxValueStep: ConversationStep = {
  fieldKey: 'valorMaximo',
  question: 'Qual o **valor máximo em R$**? (deixe em branco se ilimitado)',
  skipIf: (v) => v['criterioDesconto'] !== '2',
};

const discountTypeStep: ConversationStep = {
  fieldKey: 'tipoDesconto',
  question: 'Qual o **tipo de desconto** aplicado?',
  quickReplies: [
    { label: '% no item (By_percent)', value: '1' },
    { label: 'R$ fixo no carrinho (Cart_fixed)', value: '2' },
    { label: 'R$ fixo no item (By_fixed)', value: '3' },
  ],
};

const discountValueStep: ConversationStep = {
  fieldKey: 'valorDesconto',
  question: 'Qual o **valor do desconto**? (apenas o número, sem % ou R$)',
};

const itemTypeStep: ConversationStep = {
  fieldKey: 'tipoItemIncluido',
  question: 'Quais **tipos de itens** serão incluídos na promoção?',
  quickReplies: [
    { label: 'EANs', value: '1' },
    { label: 'SKUs', value: '2' },
    { label: 'Marcas', value: '3' },
    { label: 'Categorias', value: '4' },
    { label: 'Subcategorias', value: '5' },
    { label: 'Departamentos', value: '6' },
    { label: 'Indústrias', value: '7' },
    { label: 'Famílias', value: '8' },
  ],
};

const itemsEANsStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite os **EANs** incluídos na promoção:',
  options: eanOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '1',
};

const itemsSKUsStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite os **SKUs** incluídos na promoção:',
  options: skuOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '2',
};

const itemsBrandsStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite as **marcas** incluídas na promoção:',
  options: brandOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '3',
};

const itemsCategoriesStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite as **categorias** incluídas na promoção:',
  options: categoryOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '4',
};

const itemsSubcategoriesStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite as **subcategorias** incluídas na promoção:',
  options: subcategoryOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '5',
};

const itemsDepartmentsStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite os **departamentos** incluídos na promoção:',
  options: departmentOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '6',
};

const itemsIndustriesStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite as **indústrias** incluídas na promoção:',
  options: industryOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '7',
};

const itemsFamiliesStep: ConversationStep = {
  fieldKey: 'itensIncluidos',
  question: 'Selecione ou digite as **famílias** incluídas na promoção:',
  options: familyOptions,
  multiple: true,
  skipIf: (v) => v['tipoItemIncluido'] !== '8',
};


const couponCodeStep: ConversationStep = {
  fieldKey: 'codigoCupom',
  question: 'Qual o **código do cupom**? (máx. 60 chars, sem espaços, pode usar hífen e underline)',
};

const couponQtyStep: ConversationStep = {
  fieldKey: 'qtdCupons',
  question: 'Qual a **quantidade total de resgates** disponíveis? (99999 = ilimitado)',
};

const couponUsesStep: ConversationStep = {
  fieldKey: 'usosPorCliente',
  question: 'Quantas vezes **cada cliente** pode usar o cupom? (99999 = ilimitado)',
};

const familyNameStep: ConversationStep = {
  fieldKey: 'nomeFamilia',
  question: 'Qual o **nome da família** de produtos? (agrupamento de SKUs)',
};

const familyIndustryStep: ConversationStep = {
  fieldKey: 'industriaFamilia',
  question: 'Qual a **indústria** dessa família? (apenas uma por família)',
  options: industryOptions,
};

const familyTypeStep: ConversationStep = {
  fieldKey: 'tipoFamilia',
  question: 'Qual o **tipo desta família** na promoção?',
  quickReplies: [
    { label: '1 — Requisito (obrigatória)', value: '1' },
    { label: '2 — Benefício', value: '2' },
    { label: '3 — Ambos', value: '3' },
  ],
};

const familySkusStep: ConversationStep = {
  fieldKey: 'skusFamilia',
  question: 'Liste os **SKUs da família** (EANs + caixarias):',
  options: skuOptions,
  multiple: true,
};

const familyMinQtyStep: ConversationStep = {
  fieldKey: 'qtdMinimaAtivacao',
  question: 'Qual a **quantidade mínima** de EANs para ativar a promoção nessa família?',
};

const familyMaxQtyStep: ConversationStep = {
  fieldKey: 'qtdMaximaAtivacao',
  question: 'Qual a **quantidade máxima**? (999999 = ilimitado)',
};

const familyCountStep: ConversationStep = {
  fieldKey: 'qtdFamiliasAtivacao',
  question: 'Quantas **famílias** são necessárias para ativar a promoção?',
};

const listTypeStep: ConversationStep = {
  fieldKey: 'tipoLista',
  question: 'Qual o **tipo de lista** sugerida?',
  quickReplies: [
    { label: '1 — Original', value: '1' },
    { label: '2 — Recompra', value: '2' },
  ],
};

const listPositionStep: ConversationStep = {
  fieldKey: 'posicaoLista',
  question: 'Qual a **posição da lista** dentro da seção? (UL: G=1, M=2, P=3, PP=4)',
};

const listSectionStep: ConversationStep = {
  fieldKey: 'secaoLista',
  question: 'Em qual **seção** a lista deve aparecer? (ex: Beleza e bem estar)',
};

const hasCouponStep: ConversationStep = {
  fieldKey: 'cupom',
  question: 'Esta promoção geral terá **cupom**?',
  quickReplies: [
    { label: 'Não (Rebaixa)', value: '1' },
    { label: 'Sim (Com cupom)', value: '2' },
  ],
};

export type TemplateStepMap = Record<string, ConversationStep[]>;

export const templateSteps: TemplateStepMap = {
  t1: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    discountTypeStep,
    discountValueStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    discountCriteriaStep,
    minQtyStep,
    maxQtyStep,
    minValueStep,
    maxValueStep,
    itemTypeStep,
    itemsEANsStep,
    itemsSKUsStep,
    itemsBrandsStep,
    itemsCategoriesStep,
    itemsSubcategoriesStep,
    itemsDepartmentsStep,
    itemsIndustriesStep,
    itemsFamiliesStep,
  ],
  t2: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    { fieldKey: 'numeroCard', question: 'Qual o **número do card** nesta promoção progressiva?', quickReplies: [{ label: '0 — Card único', value: '0' }, { label: '1 — Card pai', value: '1' }, { label: '2 — Card filho 2', value: '2' }, { label: '3 — Card filho 3', value: '3' }] },
    discountValueStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    discountCriteriaStep,
    minQtyStep,
    maxQtyStep,
    minValueStep,
    maxValueStep,
    itemTypeStep,
    itemsEANsStep,
    itemsSKUsStep,
    itemsBrandsStep,
    itemsCategoriesStep,
    itemsSubcategoriesStep,
    itemsDepartmentsStep,
    itemsIndustriesStep,
    itemsFamiliesStep,
  ],
  t3: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    { fieldKey: 'numeroCard', question: 'Qual o **número do card** (uma linha por família)?', quickReplies: [{ label: '0 — Card único', value: '0' }, { label: '1 — Card pai', value: '1' }, { label: '2 — Card filho 2', value: '2' }, { label: '3 — Card filho 3', value: '3' }, { label: '4 — Card filho 4', value: '4' }] },
    discountValueStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    familyTypeStep,
    familyNameStep,
    familyIndustryStep,
    familySkusStep,
    familyMinQtyStep,
    familyMaxQtyStep,
    familyCountStep,
  ],
  t4a: [
    listTypeStep,
    { ...listTypeStep, fieldKey: 'recompraDisponivel', question: 'Esta lista original pode gerar **recompra**?', quickReplies: [{ label: 'Sim', value: 'Yes' }, { label: 'Não', value: 'No' }] },
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    familyNameStep,
    familyIndustryStep,
    familySkusStep,
    listPositionStep,
    listSectionStep,
  ],
  t4b: [
    { fieldKey: 'idListaOriginal', question: 'Qual o **ID da lista original** que origina esta recompra?', },
    promoNameStep,
    displayNameStep,
    descriptionStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    sellersStep,
    sellersExceptionStep,
    { ...cnpjsStep, fieldKey: 'cnpjsIncluidos', question: 'Informe o **ID da segmentação da lista original** (campo CNPJs incluídos tem semântica diferente na recompra):' },
    channelsStep,
    orderTypeStep,
    familyNameStep,
    familyIndustryStep,
    familySkusStep,
    listPositionStep,
    listSectionStep,
  ],
  t5: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    discountTypeStep,
    discountValueStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    discountCriteriaStep,
    minQtyStep,
    maxQtyStep,
    minValueStep,
    maxValueStep,
    couponCodeStep,
    couponQtyStep,
    couponUsesStep,
    itemTypeStep,
    itemsEANsStep,
    itemsSKUsStep,
    itemsBrandsStep,
    itemsCategoriesStep,
    itemsSubcategoriesStep,
    itemsDepartmentsStep,
    itemsIndustriesStep,
    itemsFamiliesStep,
  ],
  t6: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    { fieldKey: 'numeroCard', question: 'Qual o **número do card** nesta faixa progressiva?', quickReplies: [{ label: '1 — Card pai', value: '1' }, { label: '2 — Card filho 2', value: '2' }, { label: '3 — Card filho 3', value: '3' }] },
    discountTypeStep,
    discountValueStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    discountCriteriaStep,
    minQtyStep,
    maxQtyStep,
    minValueStep,
    maxValueStep,
    couponCodeStep,
    couponQtyStep,
    couponUsesStep,
    itemTypeStep,
    itemsEANsStep,
    itemsSKUsStep,
    itemsBrandsStep,
    itemsCategoriesStep,
    itemsSubcategoriesStep,
    itemsDepartmentsStep,
    itemsIndustriesStep,
    itemsFamiliesStep,
  ],
  t7: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    { ...discountValueStep, question: 'Qual o **valor do cashback**? (sem R$ ou %, ex: 250)' },
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    discountCriteriaStep,
    minQtyStep,
    maxQtyStep,
    minValueStep,
    maxValueStep,
    couponCodeStep,
    couponQtyStep,
    couponUsesStep,
    itemTypeStep,
    itemsEANsStep,
    itemsSKUsStep,
    itemsBrandsStep,
    itemsCategoriesStep,
    itemsSubcategoriesStep,
    itemsDepartmentsStep,
    itemsIndustriesStep,
    itemsFamiliesStep,
  ],
  t8: [
    promoNameStep,
    displayNameStep,
    descriptionStep,
    internalDescStep,
    { fieldKey: 'numeroCard', question: 'Qual o **número do card** nesta promoção?', quickReplies: [{ label: '0 — Card único', value: '0' }, { label: '1 — Card pai', value: '1' }, { label: '2 — Card filho', value: '2' }] },
    discountTypeStep,
    discountValueStep,
    industryStep,
    multiSponsorStep,
    sponsorStep,
    cumulativeStep,
    startDateStep,
    endDateStep,
    budgetLinkedStep,
    budgetValueStep,
    sellersStep,
    sellersExceptionStep,
    cnpjsStep,
    channelsStep,
    orderTypeStep,
    discountCriteriaStep,
    minQtyStep,
    maxQtyStep,
    minValueStep,
    maxValueStep,
    hasCouponStep,
    { ...couponCodeStep, skipIf: (v: FormValues) => v['cupom'] !== '2' },
    { ...couponQtyStep, skipIf: (v: FormValues) => v['cupom'] !== '2' },
    { ...couponUsesStep, skipIf: (v: FormValues) => v['cupom'] !== '2' },
    { fieldKey: 'industriasInclusao', question: 'Quais **indústrias incluir** na promoção? (opcional)', options: industryOptions, multiple: true },
    { fieldKey: 'marcasInclusao', question: 'Quais **marcas incluir**? (opcional)', options: brandOptions, multiple: true },
    { fieldKey: 'skusInclusao', question: 'Quais **SKUs incluir**? (opcional)', options: skuOptions, multiple: true },
  ],
};
